/**
 * Credit Product List Component
 * Displays credit offers (non-installment) for a product
 */
import { useRouter } from 'next/navigation'
import { FC, useCallback, useState } from 'react'

import { AdaptedVariant, buildCreditUrl, CreditItem, ShopSelector } from '@/entities/Payments'

import { BASE_URL } from '@/shared/lib/const/env'
import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { Margin } from '@/shared/ui/margin'

import cls from './style.module.scss'

interface CreditProductListProps {
	creditData: AdaptedVariant[]
	price: number
	productId: number
	productKod: string
}

/** Default credit period in months */
const CREDIT_PERIOD_MONTHS = 36

/** Credit calculation coefficient for monthly payment estimation */
const CREDIT_COEFFICIENT = 0.0359

/**
 * Format credit payment display text
 */
const formatCreditPayment = (price: number): string => `от ${formatPrice(price)}/мес.`

/**
 * Credit Product List
 * Displays available credit offers with provider selection
 */
export const CreditSelection: FC<CreditProductListProps> = ({ price, creditData, productId }) => {
	const router = useRouter()
	const [activeBank, setActiveBank] = useState(creditData[0]?.id || '')

	/**
	 * Navigate to order page with credit payment selected
	 */
	const handleApplyCredit = useCallback(() => {
		const url = buildCreditUrl(productId, activeBank)
		router.push(url)
	}, [activeBank, productId, router])

	if (!creditData.length) {
		return null
	}

	const estimatedMonthlyPayment = Math.round(price * CREDIT_COEFFICIENT)

	return (
		<div className={cls.content}>
			<p className={cls.title}>Кредит</p>
			<Margin mb="4x">
				<ShopSelector items={creditData} onChange={setActiveBank} value={activeBank} baseUrl={BASE_URL} />
			</Margin>
			<CreditItem
				period={`до ${CREDIT_PERIOD_MONTHS} мес.`}
				payCount={formatCreditPayment(estimatedMonthlyPayment)}
				firstPay={0}
				onApply={handleApplyCredit}
			/>
		</div>
	)
}
